import { useEffect } from 'react';
import { ListOpportunitiesDTO } from '@modules/opportunities/entities/opportunity/api/dto/listOpportunities.dto';
import { useNavigate } from '@tanstack/react-router';
import {
  Box, Flex, Text, Pagination
} from '@huspy/briks-web';
import { trackAmplitudeEvent } from '@shared/analytics/amplitude';
import { CasesRoute } from '@modules/opportunities/presentation/routes';
import { CaseProgressionSlido } from '@modules/opportunities/presentation/pages/CaseCreation/features/CaseProgressionSlido';
import { useDisclosure } from '@mantine/hooks';
import Table from '@components/v2/Table';
import { useTranslation } from 'react-i18next';
import { getCurrentShownRange } from '@components/Table';
import { useGlobalStore } from '@modules/core/store';
import useClientOpportunity from '@modules/opportunities/entities/opportunity/query/useClientOpportunity';
import { EmptyTemplateComponent } from '@modules/core/components/EmptyTemplateComponent';
import { CaseTableRow } from './CaseRow';

type Props = {
  data?: ListOpportunitiesDTO;
  isLoading?: boolean;
};

const PAGE_SIZE = 7;

const AutoOpenCaseProgressionSlido = () => {
  const { opportunity_id, bank_application_id } = CasesRoute.useSearch();
  const { data: requestedOpportunityData } = useClientOpportunity(opportunity_id);
  const [isOpen, { open, close }] = useDisclosure(false);
  const { user } = useGlobalStore();
  useEffect(() => {
    if (requestedOpportunityData) {
      open();
    }
  }, [requestedOpportunityData, open]);

  if (!requestedOpportunityData) {
    return null;
  }

  const isCaseOwner = user?.externalId
    === requestedOpportunityData.opportunity.referrer_external_id;
  return (
    <CaseProgressionSlido
      opportunityId={ requestedOpportunityData.opportunity.opportunity_external_id }
      bankApplications={ requestedOpportunityData.opportunity.bank_applications }
      isOpen={ isOpen }
      close={ close }
      isCaseOwner={ isCaseOwner }
      selectedBankApplicationId={ bank_application_id }
    />
  );
};

const EmptyCases = () => {
  const { t } = useTranslation();

  return (
    <Box w='100%'>
      <EmptyTemplateComponent
        title={ t('opportunity.caseList.v2.empty.title') }
        description={ t('opportunity.caseList.v2.empty.description') }
        cta={ null }
      />
    </Box>
  );
};

const CasesTable = ({ data, isLoading }: Props) => {
  const opportunities = data?.opportunities;
  const pagination = data?.paging;
  const { order_by, order, page } = CasesRoute.useSearch();
  const navigate = useNavigate({ from: CasesRoute.fullPath });
  const { t } = useTranslation();

  const currentlyShowingText = getCurrentShownRange(
    pagination?.page_index,
    PAGE_SIZE,
    pagination?.total_records_count
  );

  useEffect(() => {
    trackAmplitudeEvent('Cases Sidebar Tab Clicked');
  }, []);

  const handlePageSelect = (value: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: value,
      }),
    });
  };

  const handleSortableHeadClick = (sortBy: string) => {
    if (sortBy === order_by) {
      navigate({
        search: ({ order: _order, order_by: _order_by, ...prev }) => ({
          ...(_order === 'DESC'
            ? { ...prev }
            : {
              ...prev,
              order_by: sortBy,
              order: order === 'ASC' ? 'DESC' : 'ASC',
            }),
        }),
      });
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          order_by: sortBy,
          order: 'ASC',
        }),
      });
    }
  };

  if (opportunities?.length === 0 && !isLoading) {
    return <EmptyCases />;
  }

  return (
    <Box w='100%'>
      <Table.Root>
        <Table.Head bg='blue.50'>
          <Table.Row>
            <Table.SortableHeader
              isSortedByThis={ order_by === 'main_applicant_name' }
              order={ order as 'ASC' | 'DESC' }
              onClick={ () => handleSortableHeadClick('main_applicant_name') }
            >
              {t('opportunity.caseList.v2.tableHeader.case')}
            </Table.SortableHeader>
            <Table.Header>
              {t('opportunity.caseList.v2.tableHeader.banks')}
            </Table.Header>
            <Table.Header>
              {t('opportunity.caseList.v2.tableHeader.primaryBankStatus')}
            </Table.Header>
            <Table.Header>
              {t('opportunity.caseList.v2.tableHeader.primaryBankLoanAmount')}
            </Table.Header>
            <Table.Header>
              {t('opportunity.caseList.v2.tableHeader.createdBy')}
            </Table.Header>
            <Table.Header>
              {t('opportunity.caseList.v2.tableHeader.createdOn')}
            </Table.Header>
            <Table.Header w='100px'>{/* Actions */}</Table.Header>
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {isLoading ? (
            <Table.Loading rows={ PAGE_SIZE - 2 } cells={ 7 } />
          ) : (
            <>
              {opportunities?.map((opportunity) => (
                <CaseTableRow
                  key={ opportunity.opportunity_external_id }
                  opportunity={ opportunity }
                />
              ))}
            </>
          )}
        </Table.Body>
      </Table.Root>

      <Flex pt='spacing-5' justify='flex-end' align='center' gap='5'>
        <Text size='md' color='text-tertiary'>
          Showing
          {' '}
          {currentlyShowingText}
          {' '}
          of
          {' '}
          {pagination?.total_records_count ?? 0}
          {' '}
          items
        </Text>
        <Pagination
          count={ pagination?.total_records_count ?? 0 }
          pageSize={ pagination?.page_size }
          siblingCount={ 1 }
          defaultPage={ page }
          page={ page }
          onPageChange={ (change: { page: number; pageSize: number }) => {
            handlePageSelect(change.page);
          } }
        />
      </Flex>
      <AutoOpenCaseProgressionSlido />
    </Box>
  );
};

export default CasesTable;
